import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import emailSentIcon from 'assets/images/email_sent.svg';
import UIOtpInput from 'components/core/otp-input/otp-input';
import { User } from '../../../../../../src/types/user-type';
import { useToast } from 'hooks/use-toast';
import useResendOTPTime from 'hooks/use-resend-otp';
import {
  useGenerateOTP,
  useResendOtp,
  useVerifyOTP,
} from '../../../hooks/use-mfa';
import { VerifyOTP } from '../../../types/mfa.types';
import { useTranslation } from 'react-i18next';

/**
 * `EmailVerification` component is used to handle the verification process of the user's email address via OTP.
 * It allows the user to enter the verification code received in their email, resend the OTP if necessary,
 * and proceed with the setup once the OTP is verified successfully.
 *
 * @component
 * @example
 * const userInfo = {
 *   email: 'user@example.com',
 *   itemId: '12345',
 *   userMfaType: 1
 * };
 *
 * <EmailVerification userInfo={userInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {User | undefined} props.userInfo - The user information object containing the email and itemId for OTP generation
 * @param {Function} props.onClose - Callback function to close the dialog/modal after cancel or successful verify
 *
 * @returns {React.Element} The rendered component
 */

type EmailVerificationProps = {
  userInfo: User | undefined;
  onClose: () => void;
};

const OTP_ERROR_DEFAULTS: Record<string, string> = {
  invalid_two_factor_code: 'Invalid two-factor code',
  invalid_totp_code: 'TOTP code is invalid',
};

const getVerifyApiErrorKey = (source: {
  errors?: { message?: string };
  error?: unknown;
} | undefined) => {
  if (source?.errors?.message) return source.errors.message;
  if (typeof source?.error === 'string' && source.error) return source.error;
  return '';
};

export const EmailVerification: React.FC<Readonly<EmailVerificationProps>> = ({
  userInfo,
  onClose,
}) => {
  const { toast } = useToast();
  const [mfaId, setMfaId] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const { mutate: generateOTP, mutateAsync: generateOTPAsync } = useGenerateOTP();
  const { mutate: verifyOTP, isPending: verifyOtpPending } = useVerifyOTP();
  const { mutate: resendOtp } = useResendOtp();
  const [newMfaId, setNewMfaId] = useState<string | null>(null);
  const { t } = useTranslation();

  const {
    formattedTime,
    isResendDisabled,
    handleResend: handleResendOTP,
  } = useResendOTPTime({
    initialTime: 120,
    onResend: () => {
      if (!userInfo) return;

      if (mfaId) {
        resendOtp(mfaId, {
          onSuccess: (data) => {
            if (data?.mfaId) {
              setNewMfaId(data.mfaId);
            }
          },
        });
      } else {
        generateOTP(
          { userId: userInfo.itemId, mfaType: 2 },
          {
            onSuccess: (data) => {
              if (data?.mfaId) {
                setMfaId(data.mfaId);
                toast({
                  variant: 'success',
                  title: t('OTP_SENT'),
                  description: t('NEW_VERIFICATION_CODE_SENT'),
                });
              }
            },
            onError: () => {
              toast({
                variant: 'destructive',
                title: t('RESEND_FAILED'),
                description: `${t('FAILED_SEND_NEW_VERIFICATION_CODE')} ${t('PLEASE_TRY_AGAIN')}`,
              });
            },
          }
        );
      }
    },
  });

  useEffect(() => {
    const userId = userInfo?.itemId;
    if (!userId) return;

    let cancelled = false;

    void generateOTPAsync({ userId, mfaType: 2 })
      .then((res) => {
        if (!cancelled && res?.mfaId) {
          setMfaId(res.mfaId);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [generateOTPAsync, userInfo?.itemId]);

  const onVerify = useCallback((event?: MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!mfaId) {
      toast({
        variant: 'destructive',
        title: t('SETUP_INCOMPLETE'),
        description: t('PLEASE_GENERATE_OTP'),
      });
      return;
    }

    const verifyPayload: VerifyOTP = {
      verificationCode: otpValue,
      mfaId: newMfaId ?? mfaId ?? '',
      authType: 2,
    };

    verifyOTP(verifyPayload, {
      onSuccess: (res) => {
        const errorKey = getVerifyApiErrorKey(res);
        const isRejected =
          Boolean(errorKey) ||
          res?.isSuccess === false ||
          (res?.isValid === false && res?.isSuccess !== true) ||
          res?.valid === false;
        if (isRejected) {
          setOtpError(errorKey || 'INVALID_OTP');
          return;
        }
        toast({
          variant: 'success',
          title: t('EMAIL_VERIFICATION_LINKED_SUCCESS', 'Email verification added successfully.'),
        });
        onClose();
      },
      onError: (error) => {
        const body = error?.error as
          | { errors?: { message?: string }; error?: string; error_description?: string }
          | undefined;
        setOtpError(getVerifyApiErrorKey(body) || 'VERIFICATION_FAILED');
      },
    });
  }, [mfaId, otpValue, newMfaId, verifyOTP, toast, onClose, t]);

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent nested hideClose className="rounded-md sm:max-w-[432px] overflow-y-auto max-h-screen">
        <DialogHeader>
          <div className="flex items-center justify-center w-full">
            <div className="w-[120px] h-[108px]">
              <img src={emailSentIcon} alt="emailSentIcon" className="w-full h-full object-cover" />
            </div>
          </div>
          <DialogTitle className="!mt-6 text-2xl">{t('EMAIL_SENT')}</DialogTitle>
          <DialogDescription className="text-sm text-high-emphasis">
            {t('WEVE_SENT_VERIFICATION_KEY')}
            <span className="font-semibold">({userInfo?.email})</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col gap-4">
          <div className="flex items-center gap-1 text-sm font-normal">
            <span className="text-high-emphasis">{t('DID_NOT_RECEIVE_MAIL')}</span>
            <Button
              variant="ghost"
              size="sm"
              className={`${isResendDisabled ? 'text-sm font-normal' : 'font-semibold'}`}
              disabled={isResendDisabled}
              onClick={handleResendOTP}
            >
              {isResendDisabled ? `${t('RESEND_IN')} ${formattedTime}` : t('RESEND')}
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            <p className="font-sm text-high-emphasis font-normal">
              {t('PLEASE_ENTER_KEY')}
            </p>
            <div className="flex flex-col gap-1">
              <UIOtpInput
                numInputs={5}
                value={otpValue}
                inputStyle={otpError && '!border-error !text-destructive'}
                onChange={(value) => {
                  setOtpValue(value);
                  setOtpError('');
                }}
              />
              {otpError && (
                <span className="text-destructive text-xs">
                  {t(otpError, OTP_ERROR_DEFAULTS[otpError] ?? otpError)}
                </span>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-5 flex justify-end gap-3">
          <Button variant="outline" className="min-w-[118px]" onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            type="button"
            onClick={onVerify}
            disabled={verifyOtpPending || otpValue.length < 5 || !mfaId}
            className="min-w-[118px]"
          >
            {verifyOtpPending ? t('VERIFYING') : t('VERIFY')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
